import { ReservedBooking } from "@/gql/graphql";
import {
  formatDate,
  formatHoursMinutesWithColon,
} from "@/lib/formatters/DateFormatter";
import { formatCustomerIndexLabel } from "@/lib/formatters/CustomerIndexFormatter";
import { formatThousandsAndDecimals } from "@/lib/formatters/numbers";
import { Indent, Txt } from "@stenajs-webui/core";
import {
  StandardTableConfigWithNoGroups,
  createColumnConfig,
} from "@stenajs-webui/grid";
import { formatAmountWithCurrency } from "../lib/rebateAmountUtils";

export type RebateBookingsColumnKeys =
  | "bookingNumber"
  | "departure"
  | "vehicleType"
  | "length"
  | "weight"
  | "weightSpacing"
  | "vehicleRegNumber"
  | "trailerRegNumber"
  | "customer"
  | "customerReference"
  | "invoiceNumber"
  | "invoicedAmount"
  | "rebateAmountReserved";

const formatDeparture = (booking: ReservedBooking) => {
  const date = formatDate(booking.departureDate);

  return booking.departureTime
    ? `${date} ${formatHoursMinutesWithColon(booking.departureTime)}`
    : date;
};

// Same "index (number)" label as the Route Targets page.
const formatCustomer = (booking: ReservedBooking) =>
  formatCustomerIndexLabel(
    booking.customerNumber,
    booking.customerIndex ?? undefined
  );

export const rebateBookingsTableConfig: StandardTableConfigWithNoGroups<
  ReservedBooking,
  RebateBookingsColumnKeys
> = {
  keyResolver: (item) => String(item.bookingNumber),
  disableInfiniteList: true,
  initialSortOrder: "departure",
  columns: {
    bookingNumber: createColumnConfig((item) => item.bookingNumber, {
      columnLabel: "Booking no.",
      minWidth: "90px",
    }),
    // The date alone would leave departures on the same day unordered. The time
    // can come unpadded ("930"), which would sort after "1015" as a string.
    departure: createColumnConfig(
      (item) =>
        `${item.departureDate} ${item.departureTime?.padStart(4, "0") ?? ""}`,
      {
        columnLabel: "Departure",
        minWidth: "130px",
        itemLabelFormatter: (_, item) => formatDeparture(item),
      }
    ),
    vehicleType: createColumnConfig((item) => item.vehicleType ?? "", {
      columnLabel: "Vehicle type",
      minWidth: "90px",
    }),
    length: createColumnConfig((item) => item.length, {
      columnLabel: "Length (m)",
      minWidth: "80px",
      justifyContentCell: "flex-end",
      justifyContentHeader: "flex-end",
      renderCell: ({ item }) => (
        <Indent>
          <Txt>{formatThousandsAndDecimals(item.length, 1, 1)}</Txt>
        </Indent>
      ),
    }),
    weight: createColumnConfig((item) => item.weight, {
      columnLabel: "Weight (kg)",
      minWidth: "90px",
      justifyContentCell: "flex-end",
      justifyContentHeader: "flex-end",
      renderCell: ({ item }) => (
        <Indent>
          <Txt>{formatThousandsAndDecimals(item.weight, 0)}</Txt>
        </Indent>
      ),
    }),
    // Weight is right-aligned and vehicle reg. left-aligned, so their values
    // meet with only the cell padding between them. The grid cannot indent a
    // header, so an empty column adds space without moving cells off their
    // headers.
    weightSpacing: createColumnConfig(() => "", {
      columnLabel: "",
      minWidth: "24px",
      renderCell: () => null,
    }),
    vehicleRegNumber: createColumnConfig(
      (item) => item.vehicleRegNumber ?? "",
      {
        columnLabel: "Vehicle reg.",
        minWidth: "90px",
      }
    ),
    trailerRegNumber: createColumnConfig(
      (item) => item.trailerRegNumber ?? "",
      {
        columnLabel: "Trailer reg.",
        minWidth: "90px",
      }
    ),
    customer: createColumnConfig(formatCustomer, {
      columnLabel: "Customer",
      minWidth: "130px",
    }),
    customerReference: createColumnConfig(
      (item) => item.customerReference ?? "",
      {
        columnLabel: "Cust. ref.",
        minWidth: "100px",
      }
    ),
    invoiceNumber: createColumnConfig((item) => item.invoiceNumber ?? 0, {
      columnLabel: "Invoice no.",
      minWidth: "90px",
      // The value is 0 when missing so it sorts first; show a dash instead.
      itemLabelFormatter: (_, item) => String(item.invoiceNumber ?? "-"),
    }),
    invoicedAmount: createColumnConfig((item) => item.invoicedAmount, {
      columnLabel: "Invoiced",
      minWidth: "110px",
      justifyContentCell: "flex-end",
      justifyContentHeader: "flex-end",
      renderCell: ({ item }) => (
        <Indent>
          <Txt>
            {formatAmountWithCurrency(
              item.invoicedAmount,
              item.invoiceCurrencyCode
            )}
          </Txt>
        </Indent>
      ),
    }),
    rebateAmountReserved: createColumnConfig(
      (item) => item.rebateAmountReserved,
      {
        columnLabel: "Reserved",
        minWidth: "110px",
        justifyContentCell: "flex-end",
        justifyContentHeader: "flex-end",
        renderCell: ({ item }) => (
          <Indent>
            <Txt>
              {formatAmountWithCurrency(
                item.rebateAmountReserved,
                item.invoiceCurrencyCode
              )}
            </Txt>
          </Indent>
        ),
      }
    ),
  },
  columnOrder: [
    "bookingNumber",
    "departure",
    "vehicleType",
    "length",
    "weight",
    "weightSpacing",
    "vehicleRegNumber",
    "trailerRegNumber",
    "customer",
    "customerReference",
    "invoiceNumber",
    "invoicedAmount",
    "rebateAmountReserved",
  ],
};
